import { Button } from '@99designs/common/components/Button';
import { Spinner } from '@99designs/common/components/Spinner';
import { FormattedReactMessage } from '@99designs/common/i18n/index';
import { Field } from 'common/components/Form';
import { signup } from 'launch/common/xhr/accounts';
import Link from 'launch/components/Link';
import TermsLink from 'launch/components/TermsLink';
import React from 'react';
import Checkbox from './Checkbox';
import Paragraph from './Paragraph';

interface Props {
    title: React.ReactNode;
    onSuccess: () => void;
    postLogin?: () => Promise<void>;
    email: string;
    onError?: () => void;
    source?: string;
    ctaColor?: string;
    ctaMessage?: string;
}

interface State {
    errors: { [key: string]: string };
    email: string;
    password: string;
    passwordConfirm: string;
    busy: boolean;
    hasAgreedToPrivacyAndTerms: boolean;
}

class AuthSignupForm extends React.Component<Props, State> {
    public static defaultProps: Partial<Props> = {
        onError: () => null,
    };

    email: HTMLInputElement | null;
    componentDidMount() {
        if (this.email) {
            this.email.focus();
        }
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            errors: {},
            password: '',
            email: props.email,
            passwordConfirm: '',
            busy: false,
            hasAgreedToPrivacyAndTerms: false,
        };
    }

    attachTo = <T extends keyof State>(name: T) => {
        return {
            value: this.state[name],
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                this.setState({
                    ...this.state,
                    [name]: e.target.value,
                });
            },
        };
    };

    handlePrivacy = (checked: boolean) => {
        this.setState({ hasAgreedToPrivacyAndTerms: checked });
    };

    handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const { onSuccess, onError, source, postLogin } = this.props;
        const { email, password, passwordConfirm, hasAgreedToPrivacyAndTerms } = this.state;

        const genericErrorMessage = __(
            'Uh oh. Unfortunately, we’ve got a slight problem. Please try again in a few minutes.'
        );

        if (!hasAgreedToPrivacyAndTerms) {
            return this.setState(
                {
                    errors: { email: genericErrorMessage },
                },
                onError
            );
        }

        if (password != passwordConfirm) {
            return this.setState(
                {
                    errors: { password: __("Your passwords don't match") },
                },
                onError
            );
        }

        this.setState({ busy: true });

        try {
            await signup({ email, password, source, hasAgreedToPrivacyAndTerms });

            if (postLogin) {
                await postLogin();
            }
        } catch (errors) {
            return this.setState(
                {
                    errors: errors || { email: genericErrorMessage },
                    busy: false,
                },
                onError
            );
        }

        onSuccess();
    };

    render() {
        const { title, ctaColor, ctaMessage } = this.props;

        return (
            <form onSubmit={this.handleOnSubmit} className="modal-form modal-form--wide">
                <div className="modal-form__title">{title}</div>
                <div className="modal-form__input">
                    <Field errorMessage={this.state.errors.email} variations={['collapse']}>
                        <input
                            name="email"
                            placeholder={__('Email address')}
                            ref={input => (this.email = input)}
                            className="input-text"
                            {...this.attachTo('email')}
                        />
                    </Field>

                    <Field errorMessage={this.state.errors.password} variations={['collapse']}>
                        <input
                            name="password"
                            type="password"
                            placeholder={__('Password')}
                            className="input-text"
                            {...this.attachTo('password')}
                        />
                    </Field>

                    <Field>
                        <input
                            ref="confirm"
                            name="passwordConfirm"
                            type="password"
                            placeholder={__('Confirm Password')}
                            className="input-text"
                            {...this.attachTo('passwordConfirm')}
                        />
                    </Field>
                </div>

                <div className="modal-form__submit">
                    <Paragraph color="soft">
                        <Checkbox
                            checked={this.state.hasAgreedToPrivacyAndTerms}
                            onChange={d => this.handlePrivacy(d.checked)}
                        >
                            <FormattedReactMessage
                                message={__(
                                    'I have read and agree to the <termLink>Terms of Use</termLink> and <privacyLink>Privacy Policy</privacyLink>'
                                )}
                                replacements={{
                                    termLink: (
                                        <Link
                                            noWrap={true}
                                            href="/legal/terms-of-use"
                                            target="_blank"
                                            variations={['default']}
                                        />
                                    ),
                                    privacyLink: (
                                        <Link
                                            noWrap={true}
                                            href="/legal/privacy"
                                            target="_blank"
                                            variations={['default']}
                                        />
                                    ),
                                }}
                            />
                        </Checkbox>
                    </Paragraph>
                    <div style={{ color: ctaColor ? ctaColor : 'inherit' }}>
                        <Button
                            disabled={!this.state.hasAgreedToPrivacyAndTerms || this.state.busy}
                            type="submit"
                            variations={['primary', ctaColor ? 'chameleon' : '']}
                        >
                            {ctaMessage ? ctaMessage : __('Register')}
                        </Button>
                        {this.state.busy && <Spinner variations={['inline', 'login']} />}
                    </div>
                </div>

                <div className="modal-form__footer">
                    <div className="field__message">
                        <span> © 99designs </span>
                        <TermsLink />
                    </div>
                </div>
            </form>
        );
    }
}

export default AuthSignupForm;
